import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiImage, FiLoader, FiTrash2, FiArrowLeft, FiArrowRight, FiUploadCloud, FiX, FiZap, FiMic, FiActivity, FiFilm, FiEdit3, FiEye, FiInfo } from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";
import { useJobHistory, formatSeconds, formatSize } from "./audioShared";
import { StudioPanel, LANGUAGES, RATIOS, inputClass, labelClass, card, Pills, Slider } from "./VideoStudioPanel";
import { takeHandoff } from "../video/script";

const MAX_PICTURES = 8;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const SCRIPT_IDEAS = [
  "Present our holiday photos: the beach, the old town and the sunset dinner, and invite friends to join the next trip.",
  "Show our new products one by one, explain what makes each special, and finish with the launch week offer.",
  "A thank-you video for our team: look back at the year, the events, the people, and what comes next.",
  "Introduce our restaurant: the dining room, the chef's dishes and the dessert table, and how to book a table.",
];
const blank = () => ({ text: "", caption: "", action: "", speaks: false, seen: "" });
let nextId = 1;

export default function ImageVideoTool() {
  const { clientId, history: everything, loading, elapsed, add, remove } = useJobHistory("video");
  const history = useMemo(() => everything.filter((job) => job.quality === "images"), [everything]);
  const running = history.find((job) => job.status === "queued" || job.status === "processing");
  const latest = history[0];

  const [voices, setVoices] = useState([]);
  const [files, setFiles] = useState([]);            // [{ id, file, url }]
  const [parts, setParts] = useState({});            // picture id -> { text, caption, action, speaks, seen }
  const [script, setScript] = useState("");
  const [look, setLook] = useState(true);
  const [ratio, setRatio] = useState("auto");
  const [language, setLanguage] = useState("en");
  const [voice, setVoice] = useState("");
  const [rate, setRate] = useState(0);
  const [board, setBoard] = useState(null);          // { title, engine, ratio } once there is a script
  const [writing, setWriting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [viewId, setViewId] = useState(null);
  const [detail, setDetail] = useState(null);
  const inputRef = useRef(null);

  // a script sent here from the Video Script Generator
  useEffect(() => {
    const sent = takeHandoff("images");
    if (sent) setScript(sent.text);
  }, []);
  const resultRef = useRef(null);
  const boardRef = useRef(null);
  const filesRef = useRef([]);
  filesRef.current = files;

  useEffect(() => {
    let alive = true;
    apiRequest("/core/tts/voices/").then(({ ok, data }) => { if (alive && ok) setVoices(data.voices || []); });
    return () => { alive = false; filesRef.current.forEach((f) => URL.revokeObjectURL(f.url)); };
  }, []);

  const languages = useMemo(() => [...new Set(voices.map((v) => v.language))].filter((l) => LANGUAGES[l]), [voices]);
  const voicesHere = useMemo(() => voices.filter((v) => v.language === language), [voices, language]);
  useEffect(() => {
    if (!voicesHere.length || voice === "none" || voicesHere.some((v) => v.id === voice)) return;
    setVoice((voicesHere.find((v) => v.gender === "female") || voicesHere[0]).id);
  }, [voicesHere, voice]);

  useEffect(() => { if (latest && latest.status === "completed") setViewId(latest.id); }, [latest?.id, latest?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!viewId) { setDetail(null); return undefined; }
    let alive = true;
    apiRequest(`/core/voice-clone/${viewId}/?client_id=${encodeURIComponent(clientId)}`).then(({ ok, data }) => { if (alive) setDetail(ok ? data : null); });
    return () => { alive = false; };
  }, [viewId, clientId]);

  // ---------------------------------------------------------------------------------------- the pictures
  const addFiles = (list) => {
    setFieldError("");
    const picked = Array.from(list || []);
    const room = MAX_PICTURES - files.length;
    const added = [];
    let problem = "";
    picked.forEach((file) => {
      if (!file.type.startsWith("image/")) { problem = `"${file.name}" is not a picture (use JPG, PNG or WebP).`; return; }
      if (file.size > MAX_FILE_BYTES) { problem = `"${file.name}" is larger than 10 MB.`; return; }
      if (added.length >= room) { problem = `A video can have up to ${MAX_PICTURES} pictures.`; return; }
      added.push({ id: nextId++, file, url: URL.createObjectURL(file) });
    });
    if (added.length) setFiles((f) => [...f, ...added]);
    if (problem) setFieldError(problem);
  };
  const removeFile = (id) => {
    const gone = files.find((f) => f.id === id);
    if (gone) URL.revokeObjectURL(gone.url);
    setFiles((f) => f.filter((x) => x.id !== id));
    setParts((p) => { const { [id]: _removed, ...rest } = p; return rest; });
  };
  const moveFile = (index, d) => setFiles((list) => {
    const next = [...list];
    const j = index + d;
    if (j < 0 || j >= next.length) return list;
    [next[index], next[j]] = [next[j], next[index]];
    return next;
  });
  const partOf = (id) => parts[id] || blank();
  const editPart = (id, patch) => setParts((p) => ({ ...p, [id]: { ...blank(), ...p[id], ...patch } }));

  // ---------------------------------------------------------------------------------------- the script
  const writeScript = async () => {
    setFieldError("");
    setError("");
    setWriting(true);
    const form = new FormData();
    files.forEach((f) => form.append("pictures", f.file));
    form.append("script", script);
    form.append("look", look ? "1" : "0");
    if (ratio !== "auto") form.append("ratio", ratio);
    const { ok, data } = await apiRequest("/core/video/image-script/", { method: "POST", body: form });
    setWriting(false);
    if (!ok) return setFieldError(data?.script || errorMessage(data));
    const next = {};
    files.forEach((f, i) => { const s = data.scenes[i] || blank(); next[f.id] = { text: s.text || "", caption: s.caption || "", action: s.action || "", speaks: !!s.speaks, seen: (data.captions || [])[i] || "" }; });
    setParts(next);
    setBoard({ title: data.title || "My video", engine: data.engine, ratio: data.ratio });
    return setTimeout(() => boardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };
  const writeMyself = () => {
    setFieldError("");
    setParts((p) => { const next = { ...p }; files.forEach((f) => { next[f.id] = next[f.id] || blank(); }); return next; });
    setBoard((b) => b || { title: "My video", engine: "you", ratio: ratio === "auto" ? "" : ratio });
    setTimeout(() => boardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  // ---------------------------------------------------------------------------------------- the video
  const makeVideo = async () => {
    setError("");
    setFieldError("");
    setSubmitting(true);
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("kind", "video");
    form.append("mode", "images");
    form.append("title", board.title);
    form.append("voice", voice === "none" ? "" : voice);
    form.append("rate", String(rate));
    if (ratio !== "auto") form.append("ratio", ratio);
    form.append("scenes", JSON.stringify(files.map((f) => { const p = partOf(f.id); return { text: p.text, caption: p.caption, action: p.action, speaks: p.speaks }; })));
    files.forEach((f) => form.append("pictures", f.file));
    const { ok, data } = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
    setSubmitting(false);
    if (!ok) { setFieldError(data?.prompt || errorMessage(data)); return; }
    setViewId(null);
    add(data);
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const view = detail && detail.status === "completed" ? detail : null;
  const segments = view?.segments && !Array.isArray(view.segments) ? view.segments : {};
  const canScript = files.length > 0 && script.trim().length >= 8 && !writing;
  const canMake = !!board && files.length > 0 && !submitting && !running;
  const shape = ratio !== "auto" ? ratio : board?.ratio;

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">Image-to-Video Generation</h2>
      <p className="text-center text-gray-400 max-w-3xl mx-auto mb-9">
        Turn your own pictures into a professional video: the AI looks at each picture and writes narration that fits it, a natural voice reads it, and the studio adds smooth camera moves, transitions, captions and music.
        You can even make the people in your pictures move.
      </p>

      <div className={`${card} space-y-8`}>
        {/* 1 - the pictures */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <span className={labelClass}>1. Your pictures <span className="normal-case tracking-normal text-gray-500 font-normal">(in the order they appear)</span></span>
            <span className="text-xs text-gray-500">{files.length} / {MAX_PICTURES}</span>
          </div>
          {files.length > 0 && (
            <ol className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              {files.map((f, i) => (
                <li key={f.id} className="rounded-xl overflow-hidden border border-navy-border bg-navy relative group">
                  <img src={f.url} alt={`Your upload number ${i + 1}`} className="w-full aspect-[4/3] object-cover" />
                  <span className="absolute top-2 left-2 w-6 h-6 rounded-full bg-black/65 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  <button type="button" onClick={() => removeFile(f.id)} className="absolute top-2 right-2 p-1.5 rounded-full bg-black/65 hover:bg-red-600" aria-label={`Remove picture ${i + 1}`}><FiX size={12} /></button>
                  <div className="flex items-center justify-between px-2 py-1.5">
                    <span className="text-[10px] text-gray-500 truncate" title={f.file.name}>{formatSize(f.file.size)}</span>
                    <span className="flex gap-1">
                      <button type="button" onClick={() => moveFile(i, -1)} disabled={i === 0} className="p-1 rounded hover:bg-white/10 disabled:opacity-30" aria-label={`Move picture ${i + 1} earlier`}><FiArrowLeft size={13} /></button>
                      <button type="button" onClick={() => moveFile(i, 1)} disabled={i === files.length - 1} className="p-1 rounded hover:bg-white/10 disabled:opacity-30" aria-label={`Move picture ${i + 1} later`}><FiArrowRight size={13} /></button>
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          )}
          {files.length < MAX_PICTURES && (
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
              role="button" tabIndex={0} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
              className={`cursor-pointer rounded-xl border-2 border-dashed px-4 ${files.length ? "py-6" : "py-12"} text-center transition-colors ${dragging ? "border-accent-light bg-accent/10" : "border-navy-border hover:border-accent-light/70"}`}
            >
              <FiUploadCloud className="mx-auto text-accent-light" size={28} />
              <p className="mt-2 text-sm text-gray-200">{files.length ? "Add more pictures" : "Click to choose your pictures, or drop them here"}</p>
              <p className="text-xs text-gray-500 mt-1">JPG, PNG or WebP - up to {MAX_PICTURES} pictures, 10 MB each</p>
            </div>
          )}
          <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        </div>

        {/* 2 - the script */}
        <div>
          <div className="flex items-center justify-between mb-2.5">
            <label htmlFor="iv-script" className="text-xs uppercase tracking-widest text-white/50 font-semibold">2. Your script <span className="normal-case tracking-normal text-gray-500 font-normal">(what the video is about)</span></label>
            <span className="text-xs text-gray-500">{script.length} / 2500</span>
          </div>
          <textarea id="iv-script" className={`${inputClass} min-h-[110px] leading-relaxed`} value={script} dir="auto" maxLength={2500} onChange={(e) => setScript(e.target.value)}
            placeholder="e.g. Present our holiday photos: the beach, the old town and the sunset dinner, and invite friends to join the next trip." />
          <div className="flex flex-wrap gap-2 mt-3">
            {SCRIPT_IDEAS.map((idea) => <button key={idea} type="button" onClick={() => setScript(idea)} className="px-3 py-1 rounded-full text-xs border border-navy-border text-gray-300 hover:border-accent-light text-start">{idea.length > 56 ? `${idea.slice(0, 54)}...` : idea}</button>)}
          </div>
          <label className="flex items-start gap-2.5 text-sm text-gray-300 mt-4 cursor-pointer"><input type="checkbox" checked={look} onChange={(e) => setLook(e.target.checked)} className="mt-0.5 accent-[#22d3ee] w-4 h-4" />
            <span><FiEye className="inline -mt-0.5" size={13} /> Let the AI look at my pictures, so each part of the script fits its picture <span className="block text-xs text-gray-500">The pictures are sent to a free AI service to be described. Untick it for private pictures - the script is then shared out in order.</span></span></label>
        </div>

        <div className="grid md:grid-cols-[1.2fr_1.6fr_1fr] gap-5 items-start">
          <div>
            <span className={labelClass}>Video shape</span>
            <Pills label="Video shape" value={ratio} onChange={setRatio} options={[["auto", "Match my pictures"], ...RATIOS.map(([k, l]) => [k, l.replace("Landscape ", "").replace("Tall ", "").replace("Square ", "")])]} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="iv-lang" className={labelClass}>Narrator language</label>
              <select id="iv-lang" className={inputClass} value={language} onChange={(e) => { setLanguage(e.target.value); setVoice(""); }}>
                {languages.length ? languages.map((l) => <option key={l} value={l}>{LANGUAGES[l]}</option>) : <option value="en">English</option>}
              </select>
            </div>
            <div>
              <label htmlFor="iv-voice" className={labelClass}>Voice</label>
              <select id="iv-voice" className={inputClass} value={voice} onChange={(e) => setVoice(e.target.value)}>
                {voicesHere.map((v) => <option key={v.id} value={v.id}>{v.name.split(" - ")[0]}{v.accent_label ? ` (${v.accent_label})` : ""}, {v.gender}</option>)}
                <option value="none">No narration (silent)</option>
              </select>
            </div>
          </div>
          <Slider label="Speaking speed" value={rate} min={-30} max={40} step={5} suffix="%" onChange={setRate} />
        </div>

        {fieldError && !board && <p className="text-sm text-amber-300">{fieldError}</p>}
        <div className="grid sm:grid-cols-[2fr_1fr] gap-3">
          <button type="button" onClick={writeScript} disabled={!canScript}
            className="flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed">
            {writing ? <><FiLoader className="animate-spin" /> {look ? "Looking at your pictures and writing..." : "Writing..."}</> : <><FiZap /> {board ? "Write the script again" : "Write the script for my pictures"}</>}
          </button>
          <button type="button" onClick={writeMyself} disabled={!files.length || writing} className="px-6 py-4 rounded-full border border-white/20 hover:border-accent-light text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"><FiEdit3 size={14} /> I'll write each part myself</button>
        </div>
        {!files.length && <p className="text-xs text-gray-500 text-center -mt-4">Add your pictures first</p>}
      </div>

      <div ref={boardRef} />
      {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 mt-6">{error}</p>}

      {/* 3 - the part of the script of each picture */}
      {board && files.length > 0 && (
        <div className={`${card} mt-8`}>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
            <div className="min-w-0 flex-1">
              <label htmlFor="iv-title" className={labelClass}>3. Video title</label>
              <input id="iv-title" className={`${inputClass} max-w-md`} value={board.title} maxLength={80} onChange={(e) => setBoard({ ...board, title: e.target.value })} dir="auto" />
            </div>
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-gray-200">Script by {board.engine === "built-in" ? "the built-in writer" : board.engine === "you" ? "you" : board.engine}{shape ? ` · ${shape}` : ""}</span>
          </div>
          {board.engine === "built-in" && <p className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-4">The AI writer was not reachable, so your script was simply shared out between the pictures in order. Check each part - you can change any of them.</p>}
          <ol className="space-y-4">
            {files.map((f, i) => {
              const p = partOf(f.id);
              return (
                <li key={f.id} className="rounded-xl border border-navy-border bg-navy/60 p-4 grid md:grid-cols-[11rem_1fr] gap-4">
                  <div>
                    <img src={f.url} alt={`Your upload number ${i + 1}`} className="w-full aspect-[4/3] object-cover rounded-lg" />
                    <p className="text-xs font-semibold mt-2">Picture {i + 1}</p>
                    {p.seen && <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">The AI sees: {p.seen}</p>}
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label htmlFor={`iv-text-${f.id}`} className={labelClass}><FiMic className="inline -mt-0.5" size={11} /> What the narrator says <span className="normal-case tracking-normal text-gray-500 font-normal">(empty = this picture is shown without words)</span></label>
                      <textarea id={`iv-text-${f.id}`} className={`${inputClass} min-h-[80px]`} value={p.text} maxLength={420} dir="auto" onChange={(e) => editPart(f.id, { text: e.target.value })} />
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <label htmlFor={`iv-act-${f.id}`} className={labelClass}><FiActivity className="inline -mt-0.5" size={11} /> What moves <span className="normal-case tracking-normal text-gray-500 font-normal">(for animation)</span></label>
                        <input id={`iv-act-${f.id}`} className={inputClass} value={p.action} maxLength={220} dir="auto" onChange={(e) => editPart(f.id, { action: e.target.value })} placeholder="e.g. waves roll in, she turns and smiles" />
                        <label className="flex items-start gap-2 text-xs text-gray-300 mt-2 cursor-pointer"><input type="checkbox" checked={p.speaks} onChange={(e) => editPart(f.id, { speaks: e.target.checked })} className="mt-0.5 accent-[#22d3ee] w-4 h-4" /> <span>The person in this picture says this line (lips follow the words)</span></label>
                      </div>
                      <div>
                        <label htmlFor={`iv-cap-${f.id}`} className={labelClass}>Short caption (optional)</label>
                        <input id={`iv-cap-${f.id}`} className={inputClass} value={p.caption} maxLength={90} dir="auto" onChange={(e) => editPart(f.id, { caption: e.target.value })} />
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
          {fieldError && <p className="text-sm text-amber-300 mt-4">{fieldError}</p>}
          <button type="button" onClick={makeVideo} disabled={!canMake}
            className="w-full mt-5 flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? <><FiLoader className="animate-spin" /> Sending...</> : running ? <><FiLoader className="animate-spin" /> Making the video...</> : <><FiFilm /> Make the video ({files.length} {files.length === 1 ? "picture" : "pictures"})</>}
          </button>
          <p className="text-xs text-gray-500 text-center mt-2">Takes a few seconds per picture (the narration is recorded). Then you can preview the video, change the look, animate the people and save it.</p>
        </div>
      )}
      <p className="text-xs text-gray-500 text-center mt-4">Only use pictures you have the right to use, and do not use private pictures with the AI-look option. Pictures of real people should be used with their permission, and AI-animated people are not real footage.</p>

      <div ref={resultRef} />
      {running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6 flex items-center gap-3">
          <FiLoader className="animate-spin text-accent-light shrink-0" size={22} />
          <div><p className="font-semibold">Making your video... {formatSeconds(elapsed)}{running.progress && <span className="text-gray-400 font-normal"> · {running.progress}</span>}</p><p className="text-sm text-gray-400">The narration of each picture is recorded. You can leave this page open.</p></div>
        </div>
      )}
      {latest && latest.status === "failed" && !running && (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-5 mt-6 text-sm text-red-200 flex items-start gap-3"><FiInfo className="mt-0.5 shrink-0" /><span>{latest.error_message || "The video could not be made. Please try again."}</span></div>
      )}
      {view && !running && segments.mode === "scenes" && <StudioPanel key={view.id} view={view} segments={segments} clientId={clientId} />}

      <div className="mt-12">
        <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Your videos</p>
        {loading ? <p className="text-sm text-gray-400">Loading...</p> : history.length === 0 ? <p className="text-sm text-gray-500">Nothing here yet. Your videos will appear here.</p> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {history.map((job) => (
              <div key={job.id} className={`rounded-xl bg-white/5 border overflow-hidden ${viewId === job.id ? "border-accent-light/60" : "border-navy-border"}`}>
                {job.status === "completed" ? (
                  <button type="button" onClick={() => setViewId(job.id)} className="block w-full relative bg-black" aria-label={`Open ${job.original_name}`}>
                    {job.images?.[0] ? <img src={job.images[0].thumb} alt="" className="w-full aspect-video object-cover" loading="lazy" /> : <div className="aspect-video flex items-center justify-center"><FiImage /></div>}
                  </button>
                ) : (
                  <div className="w-full aspect-video flex items-center justify-center text-xs text-gray-500 px-3 text-center bg-navy">
                    {job.status === "failed" ? <span className="text-red-300">{job.error_message}</span> : <span className="inline-flex items-center gap-2"><FiLoader className="animate-spin" /> Working...</span>}
                  </div>
                )}
                <div className="p-3">
                  <p className="text-xs text-gray-200 line-clamp-2 min-h-[2rem]" dir="auto">{job.original_name}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[11px] text-gray-500">{new Date(job.created_at).toLocaleDateString()}</span>
                    {job.status !== "processing" && job.status !== "queued" && <button onClick={() => { if (viewId === job.id) setViewId(null); remove(job); }} title="Delete" className="text-gray-400 hover:text-red-400"><FiTrash2 size={14} /></button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
